import type { CSSProperties } from "react";
import { isCssLength, lengthBox } from "@/purpose-sections/length";
import type { ArticleComponentConfig } from "./models";
import { articleBlockTypographyStyle } from "./image-text-style";

/** Optional layout values use the shared safe length/spacing contracts. */
export function articleLayoutStyle(
  kind: "headerHeight" | "bodyPadding",
  raw: unknown,
): CSSProperties | undefined {
  if (typeof raw !== "string") return undefined;
  const name =
    kind === "headerHeight" ? "--a-post-header-height" : "--a-body-padding";
  const parse =
    kind === "bodyPadding"
      ? lengthBox
      : (value: string) =>
          value === "auto" ||
          (isCssLength(value) && !value.endsWith("%") && parseFloat(value) >= 0)
            ? value
            : undefined;
  const [mobile, desktop = mobile] = raw
    .split("|")
    .map((value) => value.trim());
  const small = parse(mobile);
  const large = parse(desktop);
  if (small === undefined && large === undefined) return undefined;
  return { [name]: small, [`${name}-desktop`]: large } as CSSProperties;
}

export function articleHeaderStyle(
  config: ArticleComponentConfig,
): CSSProperties | undefined {
  const style: Record<string, unknown> = {
    ...articleLayoutStyle("headerHeight", config.headerHeight),
    ...articleBlockTypographyStyle(config.headerTitleStyle),
  };
  if (typeof config.headerCopyPadding === "string") {
    const [mobile, desktop = mobile] = config.headerCopyPadding
      .split("|")
      .map((value) => value.trim());
    const small = lengthBox(mobile);
    const large = lengthBox(desktop);
    if (small !== undefined) style["--a-header-copy-padding"] = small;
    if (large !== undefined) style["--a-header-copy-padding-desktop"] = large;
  }
  if (["start", "center", "end"].includes(config.headerVerticalAlign ?? ""))
    style["--a-header-align"] = {
      start: "flex-start",
      center: "center",
      end: "flex-end",
    }[config.headerVerticalAlign!];
  const width =
    typeof config.headerCopyWidth === "string"
      ? config.headerCopyWidth.trim()
      : "";
  if (width === "auto" || (isCssLength(width) && parseFloat(width) > 0)) {
    style["--a-header-copy-width"] = width === "auto" ? "none" : width;
    style["--a-header-copy-box-width"] = width === "auto" ? "auto" : "100%";
  }
  return Object.keys(style).length ? (style as CSSProperties) : undefined;
}
